/**
 * jobTitleSkills.js
 * --------------------------------------------------------------------------
 * Hand-curated map: common job titles → skills typically used in those roles.
 *
 * Why this exists: a keyword scan of CV text catches skills that are
 * EXPLICITLY mentioned. But many CVs list job titles ("Senior Director,
 * Enterprise Business at SAP, 2018-2022") and bullet points about
 * achievements ("expanded EMEA revenue 40% YoY") without listing the
 * underlying skills as such. A senior sales leader is unlikely to write
 * "I am skilled at C-Suite Engagement", they just demonstrate it.
 *
 * This module bridges that gap: if Pascal's CV mentions any of these
 * titles, we add the role's typical skill bundle to the detection set.
 *
 * Format:
 *   patterns: array of regex-friendly title fragments (case-insensitive
 *             substring match against the CV text)
 *   skillIds: skills typically used in that role
 *
 * Multiple titles can fire on one CV, that's correct, because a career
 * ladder accumulates skills.
 *
 * Bias / AI Act review: titles are checked against the role family they
 * imply, not against age/gender markers. No filtering by graduation year,
 * gap years, or career-pause indicators. Reviewer: Pascal Schumachers.
 */

export const TITLE_PATTERNS = [
  // ─── Senior sales / commercial leadership ────────────────────────────
  {
    patterns: [
      'senior director, enterprise',
      'senior director enterprise',
      'svp enterprise',
      'svp, enterprise',
      'vp enterprise',
      'vp, enterprise',
      'vice president enterprise',
      'vice president, enterprise',
      'head of enterprise sales',
      'enterprise business director',
    ],
    skillIds: [
      'enterpriseSales', 'accountManagement', 'pipelineManagement',
      'negotiation', 'crm', 'csuite', 'pAndL', 'forecastAccuracy',
      'teamLeadership', 'goToMarket', 'strategicPlanning', 'channelPartner',
      'stakeholder', 'communication',
    ],
  },
  {
    patterns: [
      'director of sales', 'sales director', 'head of sales',
      'commercial director', 'director, commercial', 'director commercial',
    ],
    skillIds: [
      'enterpriseSales', 'accountManagement', 'pipelineManagement',
      'negotiation', 'crm', 'pAndL', 'forecastAccuracy',
      'teamLeadership', 'strategicPlanning', 'communication',
    ],
  },
  {
    patterns: [
      'sales manager', 'regional sales manager', 'territory manager',
      'district sales manager',
    ],
    skillIds: [
      'enterpriseSales', 'accountManagement', 'pipelineManagement',
      'negotiation', 'crm', 'territoryPlanning', 'forecastAccuracy',
      'teamLeadership',
    ],
  },
  {
    patterns: [
      'account executive', 'senior account executive',
      'enterprise account executive', 'strategic account manager',
      'key account manager', 'global account manager',
    ],
    skillIds: [
      'enterpriseSales', 'accountManagement', 'negotiation', 'crm',
      'pipelineManagement', 'forecastAccuracy', 'communication',
    ],
  },
  {
    patterns: [
      'business development manager', 'bdm', 'business development director',
      'head of business development',
    ],
    skillIds: [
      'enterpriseSales', 'accountManagement', 'negotiation',
      'goToMarket', 'channelPartner', 'crm', 'communication',
    ],
  },
  {
    patterns: [
      'channel manager', 'partner manager', 'alliance manager',
      'head of partnerships', 'director of partnerships',
    ],
    skillIds: [
      'channelPartner', 'enterpriseSales', 'negotiation',
      'accountManagement', 'goToMarket', 'communication',
    ],
  },
  {
    patterns: [
      'sales operations', 'revenue operations', 'revops', 'sales ops manager',
    ],
    skillIds: [
      'pipelineManagement', 'forecastAccuracy', 'crm', 'analytics',
      'territoryPlanning', 'enablement',
    ],
  },
  {
    patterns: ['sales enablement'],
    skillIds: [
      'enablement', 'mentoring', 'communication', 'change',
    ],
  },

  // ─── C-level / general management ────────────────────────────────────
  {
    patterns: [
      'chief revenue officer', 'cro,', 'cro -', 'cro at',
      'chief commercial officer', 'cco',
    ],
    skillIds: [
      'enterpriseSales', 'accountManagement', 'pipelineManagement',
      'negotiation', 'csuite', 'pAndL', 'forecastAccuracy', 'teamLeadership',
      'goToMarket', 'strategicPlanning', 'enablement', 'communication',
      'stakeholder',
    ],
  },
  {
    patterns: ['chief executive officer', 'ceo,', 'ceo -', 'general manager'],
    skillIds: [
      'csuite', 'pAndL', 'strategicPlanning', 'teamLeadership',
      'stakeholder', 'communication', 'change', 'orgDesign',
    ],
  },
  {
    patterns: [
      'chief operating officer', 'coo,', 'coo -', 'head of operations',
    ],
    skillIds: [
      'pAndL', 'strategicPlanning', 'teamLeadership', 'change',
      'projectMgmt', 'orgDesign', 'communication', 'stakeholder',
    ],
  },

  // ─── HR / People ──────────────────────────────────────────────────────
  {
    patterns: [
      'chief people officer', 'cpo,', 'chief human resources officer', 'chro',
      'head of people', 'head of hr', 'vp people', 'vp human resources',
    ],
    skillIds: [
      'compStrategy', 'payEquity', 'talentMgmt', 'orgDesign',
      'employeeRelations', 'hrAnalytics', 'gdprHr', 'change',
      'strategicPlanning', 'communication', 'stakeholder',
    ],
  },
  {
    patterns: [
      'people operations manager', 'hr manager', 'hr business partner',
      'hrbp', 'human resources manager', 'people partner',
    ],
    skillIds: [
      'compStrategy', 'talentMgmt', 'employeeRelations', 'gdprHr',
      'change', 'communication', 'stakeholder',
    ],
  },
  {
    patterns: ['talent acquisition', 'recruitment manager', 'head of recruiting'],
    skillIds: [
      'talentMgmt', 'communication', 'stakeholder', 'gdprHr',
    ],
  },
  {
    patterns: [
      'compensation manager', 'reward manager', 'head of total rewards',
      'compensation and benefits',
    ],
    skillIds: [
      'compStrategy', 'payEquity', 'hrAnalytics', 'gdprHr',
      'communication', 'analytics',
    ],
  },
  {
    patterns: ['people analytics', 'hr analytics manager', 'workforce analytics'],
    skillIds: [
      'hrAnalytics', 'analytics', 'sql', 'compStrategy', 'communication',
    ],
  },

  // ─── Product / engineering management ────────────────────────────────
  {
    patterns: [
      'chief product officer', 'cpo product', 'head of product',
      'vp product', 'vp, product', 'director of product',
    ],
    skillIds: [
      'productMgmt', 'roadmap', 'userResearch', 'analytics',
      'stakeholder', 'agile', 'strategicPlanning', 'teamLeadership',
      'communication',
    ],
  },
  {
    patterns: [
      'senior product manager', 'principal product manager',
      'lead product manager', 'group product manager',
    ],
    skillIds: [
      'productMgmt', 'roadmap', 'userResearch', 'analytics',
      'stakeholder', 'agile', 'communication',
    ],
  },
  {
    patterns: [
      'product manager', 'product owner',
    ],
    skillIds: [
      'productMgmt', 'roadmap', 'stakeholder', 'agile',
      'communication',
    ],
  },
  {
    patterns: [
      'engineering manager', 'engineering director', 'head of engineering',
      'vp engineering',
    ],
    skillIds: [
      'systemDesign', 'codeReview', 'mentoring', 'agile', 'cicd',
      'teamLeadership', 'projectMgmt', 'stakeholder', 'communication',
    ],
  },
  {
    patterns: [
      'staff engineer', 'principal engineer', 'principal software engineer',
    ],
    skillIds: [
      'systemDesign', 'codeReview', 'mentoring', 'agile', 'cicd',
      'communication',
    ],
  },
  {
    patterns: [
      'senior software engineer', 'senior engineer', 'senior developer',
      'lead software engineer', 'lead engineer', 'lead developer',
      'tech lead', 'technical lead',
    ],
    skillIds: [
      'systemDesign', 'codeReview', 'mentoring', 'agile', 'cicd',
    ],
  },
  {
    patterns: ['software engineer', 'software developer', 'full stack developer', 'fullstack developer'],
    skillIds: ['agile', 'codeReview', 'cicd'],
  },
  {
    patterns: ['data engineer', 'analytics engineer'],
    skillIds: ['sql', 'analytics', 'agile', 'cicd'],
  },
  {
    patterns: ['data scientist', 'ml engineer', 'machine learning engineer'],
    skillIds: ['python', 'analytics', 'sql', 'agile'],
  },
  {
    patterns: [
      'devops engineer', 'site reliability engineer', 'sre',
      'platform engineer', 'cloud engineer',
    ],
    skillIds: ['cicd', 'docker', 'k8s', 'aws', 'systemDesign'],
  },
  {
    patterns: ['program manager', 'technical program manager', 'tpm,'],
    skillIds: ['projectMgmt', 'agile', 'stakeholder', 'communication', 'change'],
  },

  // ─── Misc business / finance ─────────────────────────────────────────
  {
    patterns: [
      'chief financial officer', 'cfo,', 'cfo -',
      'vp finance', 'finance director', 'head of finance',
    ],
    skillIds: [
      'pAndL', 'strategicPlanning', 'forecastAccuracy', 'analytics',
      'csuite', 'communication', 'stakeholder',
    ],
  },
  {
    patterns: ['consultant', 'principal consultant', 'senior consultant', 'management consultant'],
    skillIds: [
      'strategicPlanning', 'projectMgmt', 'stakeholder', 'communication',
      'analytics', 'change',
    ],
  },
  {
    patterns: ['marketing director', 'head of marketing', 'vp marketing', 'cmo,'],
    skillIds: [
      'goToMarket', 'demandGen', 'strategicPlanning', 'analytics',
      'communication', 'stakeholder',
    ],
  },
];

/**
 * Detect skills implied by the job titles found in CV text.
 *
 * Returns a Set of skill IDs that the user likely has based on titles
 * mentioned anywhere in their CV.
 *
 * The matching is intentionally conservative: substring match against
 * lowercased text. False positives are acceptable here because the
 * adjacency layer + the AI Act human-oversight footer mean nothing acts
 * on these alone.
 */
export function inferSkillsFromTitles(rawText) {
  if (!rawText) return { inferredSkillIds: new Set(), matchedTitles: [] };
  const lower = rawText.toLowerCase();
  const inferredSkillIds = new Set();
  const matchedTitles = [];

  for (const entry of TITLE_PATTERNS) {
    for (const pattern of entry.patterns) {
      if (lower.includes(pattern)) {
        matchedTitles.push(pattern);
        for (const sid of entry.skillIds) {
          inferredSkillIds.add(sid);
        }
        // Don't break, same role family might match multiple aliases,
        // but only counted once per skill anyway (Set semantics).
      }
    }
  }

  return { inferredSkillIds, matchedTitles };
}
